import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  Checkbox as CheckboxChakra,
  HStack,
  Button,
  Box,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import {
  CREATE_PAGE_GROUP,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';

interface Props {
  onCreated?: (data: any) => void;
}
interface FormData {
  name: string;
  description?: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('La fecha de inicio es obligatoria'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(
            schedule_start,
            'La fecha final debe ser posterior a la fecha de inicio',
          )
          .required('La fecha final es obligatoria');
      }
      return yup.date().required('La fecha final es obligatoria');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);

  const { errors } = formState;
  const { t } = useTranslation();
  useEffect(() => {
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, updateError, deleteError]);

  useEffect(() => {
    if (!pageGroup) return;

    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('description', pageGroup?.description);
    setValue('name', pageGroup.name);
  }, [pageGroup, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              schedule_end: scheduleData ? rest.schedule_end : null,
              countdown: scheduleData ? rest.countdown : false,
              id: pageGroup?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: t('success'),
          description: t('groupEditedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: t('success'),
          description: t('groupCreatedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(t('confirmDeleteGroup'));
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: t('success'),
          description: t('groupDeletedSuccess'),
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <Box>
      <VStack
        as="form"
        spacing="8"
        minHeight="fit-content"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <SimpleGrid
          minChildWidth="200px"
          spacing="4"
          w="100%"
          minHeight="fit-content"
        >
          <Input
            label={t('title')}
            isRequired
            error={errors?.name as FieldError | undefined}
            {...register('name')}
          />
        </SimpleGrid>
        <Textarea
          label={t('descriptionField')}
          isRequired
          error={errors?.description as FieldError | undefined}
          {...register('description')}
        />
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            {t('schedule')}
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label={t('scheduleStart')}
                showTimeSelect
                error={errors?.schedule_start as FieldError | undefined}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label={t('scheduleEnd')}
                showTimeSelect
                error={errors?.schedule_end as FieldError | undefined}
              />
            </SimpleGrid>
            <CheckboxChakra
              alignSelf="flex-start"
              colorScheme="brand"
              {...register('countdown')}
            >
              {t('countdown')}
            </CheckboxChakra>
          </>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              {t('remove')}
            </Button>
          )}
          <Button onClick={onClose}>{t('close')}</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
          >
            {t('save')}
          </Button>
        </HStack>
      </ModalFooter>
    </Box>
  );
};

export default Form;
